import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, Play, Pause, RotateCcw, Trophy, Zap, Timer } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

interface FallingValue {
    id: number;
    value: string;
    type: 'string' | 'number' | 'boolean' | 'array' | 'object';
    x: number;
    y: number;
}

const TYPE_DATA = {
    string: { color: 'from-green-400 to-green-600', examples: ['"Hello"', '"Gorilla"', '"Code"', '"Banana"', '""', '"123"'] },
    number: { color: 'from-blue-400 to-blue-600', examples: ['42', '3.14', '0', '-7', '100', '999'] },
    boolean: { color: 'from-purple-400 to-purple-600', examples: ['true', 'false'] },
    array: { color: 'from-orange-400 to-orange-600', examples: ['[1,2,3]', '["a","b"]', '[]', '[true]'] },
    object: { color: 'from-pink-400 to-pink-600', examples: ['{name:"Go"}', '{id:1}', '{}', '{x:0,y:0}'] },
};

const BUCKETS: { type: FallingValue['type']; label: string; emoji: string }[] = [
    { type: 'string', label: 'String', emoji: '📝' },
    { type: 'number', label: 'Number', emoji: '🔢' },
    { type: 'boolean', label: 'Boolean', emoji: '✅' },
    { type: 'array', label: 'Array', emoji: '📦' },
    { type: 'object', label: 'Object', emoji: '🗂️' },
];

type Difficulty = 'easy' | 'medium' | 'hard';

const DIFFICULTY_SETTINGS = {
    easy: { spawnRate: 3000, fallSpeed: 4000, types: ['string', 'number', 'boolean'] as const },
    medium: { spawnRate: 2500, fallSpeed: 3000, types: ['string', 'number', 'boolean', 'array'] as const },
    hard: { spawnRate: 2000, fallSpeed: 2500, types: ['string', 'number', 'boolean', 'array', 'object'] as const },
};

export const VariableTetris = () => {
    const { completeModule } = useAcademyStore();
    const [isPlaying, setIsPlaying] = useState(false);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => {
        const saved = localStorage.getItem('variable-tetris-highscore');
        return saved ? parseInt(saved) : 0;
    });
    const [lives, setLives] = useState(3);
    const [combo, setCombo] = useState(0);
    const [values, setValues] = useState<FallingValue[]>([]);
    const [difficulty, setDifficulty] = useState<Difficulty>('easy');
    const [gameOver, setGameOver] = useState(false);
    const [showFeedback, setShowFeedback] = useState<{ correct: boolean; x: number } | null>(null);
    const [timeElapsed, setTimeElapsed] = useState(0);

    const settings = DIFFICULTY_SETTINGS[difficulty];
    const activeBuckets = BUCKETS.filter(b => settings.types.includes(b.type as any));

    const spawnValue = useCallback(() => {
        const typeKeys = settings.types;
        const type = typeKeys[Math.floor(Math.random() * typeKeys.length)] as FallingValue['type'];
        const examples = TYPE_DATA[type].examples;
        const value = examples[Math.floor(Math.random() * examples.length)];

        const bucketWidth = 100 / activeBuckets.length;
        const x = Math.random() * (100 - 15) + 7.5;

        const newValue: FallingValue = {
            id: Date.now() + Math.random(),
            value,
            type,
            x,
            y: 0,
        };

        setValues(prev => [...prev, newValue]);
    }, [settings.types, activeBuckets.length]);

    // Spawn timer
    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(spawnValue, settings.spawnRate);
        return () => clearInterval(interval);
    }, [isPlaying, spawnValue, settings.spawnRate]);

    // Game timer
    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(() => setTimeElapsed(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, [isPlaying]);

    // Check for game over
    useEffect(() => {
        if (lives <= 0) {
            setGameOver(true);
            setIsPlaying(false);
            if (score > highScore) {
                setHighScore(score);
                localStorage.setItem('variable-tetris-highscore', score.toString());
            }
            if (score >= 100) {
                completeModule('variables-types', 100);
            }
        }
    }, [lives, score, highScore, completeModule]);

    const handleBucketClick = (bucketType: FallingValue['type'], bucketIndex: number) => {
        if (!isPlaying) return;

        // Find the lowest value that's in range of this bucket
        const bucketWidth = 100 / activeBuckets.length;
        const bucketLeft = bucketIndex * bucketWidth;
        const bucketRight = (bucketIndex + 1) * bucketWidth;

        // Find values that are low enough and in bucket range
        const catchableValues = values.filter(v => {
            const valueCenter = v.x;
            return valueCenter >= bucketLeft - 10 && valueCenter <= bucketRight + 10;
        });

        if (catchableValues.length === 0) return;

        // Get the one closest to bottom (highest y conceptually)
        const caught = catchableValues[0];

        if (caught.type === bucketType) {
            // Correct!
            const points = 10 * (combo + 1);
            setScore(s => s + points);
            setCombo(c => c + 1);
            setShowFeedback({ correct: true, x: caught.x });
        } else {
            // Wrong!
            setLives(l => l - 1);
            setCombo(0);
            setShowFeedback({ correct: false, x: caught.x });
        }

        // Remove the caught value
        setValues(prev => prev.filter(v => v.id !== caught.id));

        setTimeout(() => setShowFeedback(null), 500);
    };

    const handleMiss = (valueId: number) => {
        setLives(l => l - 1);
        setCombo(0);
        setValues(prev => prev.filter(v => v.id !== valueId));
    };

    const startGame = () => {
        setIsPlaying(true);
        setScore(0);
        setLives(3);
        setCombo(0);
        setValues([]);
        setGameOver(false);
        setTimeElapsed(0);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 font-sans text-white">
            {/* Header */}
            <div className="bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-green-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    <div className="bg-green-900/50 text-green-400 px-4 py-2 rounded-xl text-base font-bold border border-green-800">
                        🎮 Variable Tetris
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-8">
                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-5xl font-black text-white mb-3 flex items-center justify-center gap-4">
                        <Zap className="text-yellow-400" size={48} /> Variable Tetris
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Catch falling values in the correct type bucket!
                        <span className="text-green-400 font-bold"> Score 100+ to complete.</span>
                    </p>
                </div>

                {/* Score Panel */}
                <div className="flex justify-center gap-6 mb-8 flex-wrap">
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[120px]">
                        <div className="text-4xl font-black text-green-400">{score}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Score</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[120px]">
                        <div className="text-4xl font-black text-red-400 flex justify-center gap-1">
                            {Array(lives).fill('❤️').join('')}
                            {Array(Math.max(0, 3 - lives)).fill('🖤').join('')}
                        </div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Lives</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[120px]">
                        <div className="text-4xl font-black text-yellow-400">{combo}x</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Combo</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[120px]">
                        <div className="text-4xl font-black text-purple-400">{highScore}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">High Score</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[120px]">
                        <div className="text-4xl font-black text-cyan-400 flex items-center justify-center gap-2">
                            <Timer size={28} /> {timeElapsed}s
                        </div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Time</div>
                    </div>
                </div>

                {/* Difficulty Selection */}
                {!isPlaying && !gameOver && (
                    <div className="flex justify-center gap-4 mb-8">
                        {(['easy', 'medium', 'hard'] as Difficulty[]).map(d => (
                            <button
                                key={d}
                                onClick={() => setDifficulty(d)}
                                className={`px-8 py-3 rounded-2xl font-bold text-lg capitalize transition-all border-2 ${difficulty === d
                                    ? d === 'easy' ? 'bg-green-500 text-white border-green-400'
                                        : d === 'medium' ? 'bg-yellow-500 text-black border-yellow-400'
                                            : 'bg-red-500 text-white border-red-400'
                                    : 'bg-slate-800 text-slate-400 border-slate-600 hover:border-slate-500'
                                    }`}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                )}

                {/* Game Area */}
                <div className="relative bg-slate-800/50 rounded-3xl border-2 border-slate-700 overflow-hidden" style={{ height: '500px' }}>
                    {/* Falling Values */}
                    <AnimatePresence>
                        {values.map(val => (
                            <motion.div
                                key={val.id}
                                initial={{ top: '-80px', left: `${val.x}%` }}
                                animate={{ top: '85%' }}
                                exit={{ opacity: 0, scale: 0 }}
                                transition={{ duration: settings.fallSpeed / 1000, ease: 'linear' }}
                                onAnimationComplete={() => handleMiss(val.id)}
                                className="absolute -translate-x-1/2"
                                style={{ left: `${val.x}%` }}
                            >
                                <div className={`bg-gradient-to-br ${TYPE_DATA[val.type].color} px-4 py-3 rounded-xl shadow-xl border-2 border-white/20`}>
                                    <span className="font-mono font-bold text-white text-lg">{val.value}</span>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {/* Feedback */}
                    <AnimatePresence>
                        {showFeedback && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0, y: 50 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0 }}
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-6xl pointer-events-none"
                            >
                                {showFeedback.correct ? '✅' : '❌'}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Buckets */}
                    <div className="absolute bottom-0 left-0 right-0 flex">
                        {activeBuckets.map((bucket, i) => (
                            <button
                                key={bucket.type}
                                onClick={() => handleBucketClick(bucket.type, i)}
                                disabled={!isPlaying}
                                className={`flex-1 h-24 bg-gradient-to-t ${TYPE_DATA[bucket.type].color} flex flex-col items-center justify-center gap-1 border-t-4 border-white/30 transition-all hover:brightness-110 active:scale-95 disabled:opacity-50 cursor-pointer`}
                            >
                                <span className="text-3xl">{bucket.emoji}</span>
                                <span className="font-bold text-white text-lg">{bucket.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Overlay */}
                    {!isPlaying && (
                        <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-20">
                            <div className="text-center">
                                {gameOver ? (
                                    <>
                                        <div className="text-6xl mb-6">{score >= highScore && score > 0 ? '🏆' : '😵'}</div>
                                        <h2 className="text-4xl font-bold text-white mb-3">Game Over!</h2>
                                        <p className="text-xl text-slate-400 mb-2">Final Score: <span className="text-green-400 font-bold">{score}</span></p>
                                        <p className="text-lg text-slate-500 mb-6">Time: {timeElapsed}s</p>
                                        {score >= highScore && score > 0 && (
                                            <p className="text-yellow-400 font-bold text-xl mb-6">🎉 New High Score!</p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="text-6xl mb-6">🎮</div>
                                        <h2 className="text-4xl font-bold text-white mb-3">Variable Tetris</h2>
                                        <p className="text-xl text-slate-400 mb-6">Click the correct type bucket to catch values!</p>
                                    </>
                                )}
                                <button
                                    onClick={startGame}
                                    className="btn btn-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                                >
                                    <Play size={24} fill="currentColor" /> {gameOver ? 'Play Again' : 'Start Game'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Type Legend */}
                <div className="mt-8 bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                    <h3 className="font-bold text-white text-lg mb-4">📚 Type Reference</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {BUCKETS.map(b => (
                            <div key={b.type} className={`p-4 rounded-xl bg-gradient-to-br ${TYPE_DATA[b.type].color} bg-opacity-20`}>
                                <div className="text-2xl mb-1">{b.emoji}</div>
                                <div className="font-bold text-white">{b.label}</div>
                                <div className="text-sm text-white/70 font-mono">{TYPE_DATA[b.type].examples[0]}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
